// src/components/MetaTags.tsx
/* eslint-disable @typescript-eslint/no-explicit-any */
// src/components/MetaTags.tsx
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { setCanonicalUrl } from '@/lib/canonicalUrl';

interface MetaTagsProps {
  title?: string;
  description?: string;
  image?: string;
  type?: string;
  url?: string;
  canonicalUrl?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  structuredData?: Record<string, any>;
}

/**
 * A component that manages all meta tags including Open Graph, Twitter Cards, and JSON-LD structured data
 */
const MetaTags = ({
  title,
  description,
  image = '/hoptira-logo-full-dark.svg',
  type = 'website',
  url,
  canonicalUrl,
  twitterCard = 'summary_large_image',
  structuredData,
}: MetaTagsProps) => {
  const location = useLocation();
  const siteUrl = 'https://hoptira.com';
  const currentUrl = url || `${siteUrl}${location.pathname}${location.search}`;
  const imageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;

  useEffect(() => {
    // Update Open Graph meta tags
    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:image', imageUrl);
    updateMetaTag('og:url', currentUrl);
    updateMetaTag('og:type', type);
    updateMetaTag('og:site_name', 'Hoptira');

    // Update Twitter Card meta tags
    updateMetaTag('twitter:card', twitterCard);
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', imageUrl);
    updateMetaTag('twitter:site', '@hoptira');

    // Set canonical URL
    if (canonicalUrl) {
      setCanonicalUrl(canonicalUrl);
    } else {
      // If no specific canonical URL provided, use current URL without hash
      const canonicalUrlWithoutHash = currentUrl.split('#')[0];
      setCanonicalUrl(canonicalUrlWithoutHash);
    }

    // Add JSON-LD structured data
    if (structuredData) {
      updateStructuredData(structuredData);
    }

    return () => {
      // Clean up structured data when component unmounts
      const existingScript = document.getElementById('structured-data');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [
    title,
    description,
    imageUrl,
    currentUrl,
    canonicalUrl,
    type,
    twitterCard,
    structuredData,
  ]);

  // Helper function to update meta tags
  const updateMetaTag = (name: string, content?: string) => {
    if (!content) return;

    // Check if the meta tag already exists
    let meta =
      document.querySelector(`meta[property="${name}"]`) ||
      document.querySelector(`meta[name="${name}"]`);

    if (meta) {
      // Update existing tag
      meta.setAttribute('content', content);
    } else {
      // Create a new meta tag
      meta = document.createElement('meta');

      // Use property for Open Graph tags, name for others
      if (name.startsWith('og:')) {
        meta.setAttribute('property', name);
      } else {
        meta.setAttribute('name', name);
      }

      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    }
  };

  // Helper function to add/update structured data
  const updateStructuredData = (data: Record<string, any>) => {
    // Remove existing structured data if any
    const existingScript = document.getElementById('structured-data');
    if (existingScript) {
      existingScript.remove();
    }

    // Create new script element for structured data
    const script = document.createElement('script');
    script.id = 'structured-data';
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify(data);
    document.head.appendChild(script);
  };

  // This component doesn't render anything
  return null;
};

export default MetaTags;
